import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import type { FormData, SignatureData } from '../types'

export interface PdfGeneratorOptions {
  title?: string
  fontSize?: number
  lineHeight?: number
  margin?: number
}

export async function generatePdf(
  templateContent: string,
  formData: FormData,
  signature: SignatureData | null,
  options: PdfGeneratorOptions = {}
): Promise<Uint8Array> {
  const {
    fontSize = 11,
    lineHeight = 1.4,
    margin = 50,
  } = options

  const pdfDoc = await PDFDocument.create()
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  const pageWidth = 595.28
  const pageHeight = 841.89
  const contentWidth = pageWidth - margin * 2

  let processedContent = templateContent
  for (const [key, value] of Object.entries(formData)) {
    // Case-insensitive replacement for variable names
    const regex = new RegExp(`{{${key}}}`, 'gi')
    processedContent = processedContent.replace(regex, value || '')
  }

  const textContent = htmlToText(processedContent)
  const lines = wrapText(textContent, font, fontSize, contentWidth)

  let page = pdfDoc.addPage([pageWidth, pageHeight])
  let y = pageHeight - margin

  for (const line of lines) {
    if (y < margin + 50) {
      page = pdfDoc.addPage([pageWidth, pageHeight])
      y = pageHeight - margin
    }

    if (line.trim() === '[SIGNATURE_PLACEHOLDER]') {
      if (signature) {
        try {
          const signatureImage = await pdfDoc.embedPng(signature.dataUrl)
          const sigWidth = Math.min(signature.width, 200)
          const sigHeight = (signature.height / signature.width) * sigWidth

          y -= sigHeight + 10
          page.drawImage(signatureImage, {
            x: margin,
            y: y,
            width: sigWidth,
            height: sigHeight,
          })
          y -= 20
        } catch {
          page.drawText('_____________________', {
            x: margin,
            y: y - fontSize,
            size: fontSize,
            font: font,
            color: rgb(0, 0, 0),
          })
          y -= fontSize * lineHeight
        }
      } else {
        page.drawText('_____________________', {
          x: margin,
          y: y - fontSize,
          size: fontSize,
          font: font,
          color: rgb(0, 0, 0),
        })
        y -= fontSize * lineHeight
      }
      continue
    }

    const isHeading = line.startsWith('# ')
    
    if (line.trim() === '') {
      y -= fontSize * lineHeight * 0.5
      continue
    }

    // Handle inline bold markdown (**text**)
    const segments = parseInlineBold(line)
    let xOffset = margin

    for (const segment of segments) {
      const currentFont = segment.bold ? boldFont : font
      const currentSize = isHeading ? fontSize + 4 : fontSize
      const displayText = isHeading && xOffset === margin ? segment.text.slice(2) : segment.text

      page.drawText(displayText, {
        x: xOffset,
        y: y - currentSize,
        size: currentSize,
        font: currentFont,
        color: rgb(0, 0, 0),
      })

      xOffset += currentFont.widthOfTextAtSize(displayText, currentSize)
    }

    y -= (isHeading ? fontSize + 4 : fontSize) * lineHeight
  }

  return pdfDoc.save()
}

function parseInlineBold(text: string): Array<{ text: string; bold: boolean }> {
  const segments: Array<{ text: string; bold: boolean }> = []
  const parts = text.split('**')
  
  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) {
      segments.push({
        text: parts[i],
        bold: i % 2 === 1 // Odd indices are between ** markers
      })
    }
  }
  
  return segments.length > 0 ? segments : [{ text, bold: false }]
}

function htmlToText(html: string): string {
  let text = html
    .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
    .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '$1\n\n')  // Remove ## for h2
    .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '$1\n\n')  // Remove ### for h3
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
    .replace(/<b>(.*?)<\/b>/gi, '**$1**')
    .replace(/<em>(.*?)<\/em>/gi, '_$1_')
    .replace(/<i>(.*?)<\/i>/gi, '_$1_')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')

  return text.trim()
}

function wrapText(
  text: string,
  font: { widthOfTextAtSize: (text: string, size: number) => number },
  fontSize: number,
  maxWidth: number
): string[] {
  const paragraphs = text.split('\n')
  const result: string[] = []

  for (const paragraph of paragraphs) {
    if (paragraph.trim() === '') {
      result.push('')
      continue
    }

    const words = paragraph.split(' ')
    let currentLine = ''

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word
      const width = font.widthOfTextAtSize(testLine, fontSize)

      if (width > maxWidth && currentLine) {
        result.push(currentLine)
        currentLine = word
      } else {
        currentLine = testLine
      }
    }

    if (currentLine) {
      result.push(currentLine)
    }
  }

  return result
}

export function downloadPdf(pdfBytes: Uint8Array, filename: string): void {
  const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
